// Copyright (c) 2021 WiseTime. All rights reserved.

import { concat, EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import * as Actions from "../actions"
import * as ActivityTypesSlice from "../slice"
import * as UserContextSlice from "../../user-context/slice"
import * as UserContextActions from "../../user-context/actions"
import {
  ListRecentActivityTypesRequest,
  ListRecentActivityTypesResponse
} from "generated/legebill/site/activitytype/v4/activity_type_pb"
import { grpc } from "app/rpc/executor"
import { activityTypeServiceClient } from "app/rpc/client"
import { throttleSame } from "../../../lib/rxutils"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type FetchRecentActivityTypesRpc = (request: ListRecentActivityTypesRequest) => Observable<ListRecentActivityTypesResponse>

export const fetchRecentActivityTypes = (fetchRecentActivityTypesRpc: FetchRecentActivityTypesRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.fetchRecentActivityTypes.match),
      throttleSame(2000, ({ payload: { request: a }}, { payload: { request: b }}) => a.getSiteId() === b.getSiteId()),
      mergeMap(({ payload: { request, swallowFailure }}) =>
        of(request)
          .pipe(
            mergeMap(fetchRecentActivityTypesRpc),
            map(t => t.toObject()),
            map(ActivityTypesSlice.setRecentActivityTypes),
            catchError(e => handleError(e, swallowFailure)),
          )),
    )

const fetchRecentActivityTypesRpc = (request: ListRecentActivityTypesRequest): Observable<ListRecentActivityTypesResponse> =>
  grpc(() => activityTypeServiceClient.listRecentActivityTypes(request, null))

const handleError = (error: unknown, swallowFailure: boolean | undefined): Observable<Action> => {
  if (isGrpcError(error)) {
    const { code } = error
    if (code === StatusCode.PERMISSION_DENIED) {
      // clear current site id and re-fetch user (includes sites list) and membership details
      return concat(
        of(UserContextSlice.clearCurrentSiteId()),
        of(UserContextActions.fetchUserDetails()),
      )
    }
  }

  return swallowFailure ?
    EMPTY :
    of(showSnackBar({
      message: t({
        id: "msg_fetch_recent_activity_types_failed",
        message: "Failed to load recent activity types. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
      }),
      messageType: "ERROR",
    }))
}

export default fetchRecentActivityTypes(fetchRecentActivityTypesRpc)
